import { MutationTree } from 'vuex';
import State from '@/store/state';
import {
  Category,
  Institution,
  LearningObject,
  PagerResponse,
  Procedure,
  Study,
  User,
  UserRole,
  GetMostPopularProceduresResponse,
  GetInfrequentProceduresResponse,
  GetNurseAgeExperienceStatsResponse,
  GetNursesExperienceResponse,
  GetNurseAgeStatsResponse,
  GetNurseExperienceStatsResponse,
  GetAverageShiftDataResponse,
  GetAveragePatientAcuityDataResponse,
} from '@/generated/graphql';

export enum MutationTypes {
  SET_CATEGORY_LIST = 'SET_CATEGORY_LIST',
  SET_PROCEDURE_LIST = 'SET_PROCEDURE_LIST',
  SET_ACTIVE_PROCEDURE = 'SET_ACTIVE_PROCEDURE',
  SET_PROCEDURE_PAGEINFO = 'SET_PROCEDURE_PAGEINFO',
  SET_PROCEDURE_REFRESH = 'SET_PROCEDURE_REFRESH',
  SET_CURRENT_USER = 'SET_CURRENT_USER',
  SET_CURRENT_ROLES = 'SET_CURRENT_ROLES',
  SET_INSTITUTIONS_LIST = 'SET_INSTITUTIONS_LIST',
  SET_INSTITUTIONS_PAGEINFO = 'SET_INSTITUTIONS_PAGEINFO',
  SET_REFRESH_INSTITUTIONS = 'SET_REFRESH_INSTITUTIONS',
  SET_IMPERSONATED_USER = 'SET_IMPERSONATED_USER',
  SET_STUDY_LIST = 'SET_STUDY_LIST',
  SET_STUDY_RES_LIST = 'SET_STUDY_RES_LIST',
  SET_STUDY_PAGEINFO = 'SET_STUDY_PAGEINFO',
  SET_ACTIVE_STUDY = 'SET_ACTIVE_STUDY',
  SET_LEARNING_OBJECTS_LIST = 'SET_LEARNING_OBJECTS_LIST',
  SET_LEARNING_OBJECTS_LIST_PAGE_INFO = 'SET_LEARNING_OBJECTS_LIST_PAGE_INFO',
  SET_MOST_POPULAR_PROCEDURES = 'SET_MOST_POPULAR_PROCEDURES',
  SET_INFREQUENT_PROCEDURES = 'SET_INFREQUENT_PROCEDURES',
  SET_NURSES_EXPIRIENCE_RESPONSE = 'SET_NURSES_EXPIRIENCE_RESPONSE',
  SET_NURSE_AGE_EXPERIENCE_STATS = 'SET_NURSE_AGE_EXPERIENCE_STATS',
  SET_NURSE_AGE_STATS = 'SET_NURSE_AGE_STATS',
  SET_NURSE_SEX_STATS = 'SET_NURSE_SEX_STATS',
  SET_AVERAGE_SHIFT_DATA = 'SET_AVERAGE_SHIFT_DATA',
  SET_AVERAGE_ACUITY_DATA = 'SET_AVERAGE_ACUITY_DATA',
  SET_FIVE_MOST_FREQUENTLY_PROCEDURES = 'SET_FIVE_MOST_FREQUENTLY_PROCEDURES',
  SET_FIVE_LEAST_FREQUENTLY_PROCEDURES = 'SET_FIVE_LEAST_FREQUENTLY_PROCEDURES',
  SET_SURVEY_RESULTS = 'SET_SURVEY_RESULTS',
  SET_FIVE_MOST_ASSISTED_PROCEDURES = 'SET_FIVE_MOST_ASSISTED_PROCEDURES',
  SET_SHIFT_INTENSITY_CHART = 'SET_SHIFT_INTENSITY_CHART',
  SET_SHIFT_TIREDNESS_CHART = 'SET_SHIFT_TIREDNESS_CHART',
  SET_TABLE_SHOW_SIZE = 'SET_TABLE_SHOW_SIZE',
  SET_PATIENTS_PAGEINFO = 'SET_PATIENTS_PAGEINFO',
  SET_PATIENTS_LIST = 'SET_PATIENTS_LIST',
  SET_VISITS_PAGEINFO = 'SET_VISITS_PAGEINFO',
  SET_SHIFT_VISITS = 'SET_SHIFT_VISITS',
  SET_NURSE_HISTORY_LOCATION = 'SET_NURSE_HISTORY_LOCATION',
}

const mutations: MutationTree<State> = {
  [MutationTypes.SET_AVERAGE_ACUITY_DATA](state: State, payload: GetAveragePatientAcuityDataResponse) {
    state.acuityAverageStats = payload;
  },
  [MutationTypes.SET_AVERAGE_SHIFT_DATA](state: State, payload: GetAverageShiftDataResponse) {
    state.shiftAverageStats = payload;
  },
  [MutationTypes.SET_NURSE_AGE_EXPERIENCE_STATS](state: State, payload: GetNurseAgeExperienceStatsResponse) {
    state.nurseAgeExperienceStats = payload;
  },
  [MutationTypes.SET_NURSE_AGE_STATS](state: State, payload: GetNurseAgeStatsResponse) {
    state.nurseAgeStats = payload;
  },

  [MutationTypes.SET_NURSE_SEX_STATS](state: State, payload: GetNurseExperienceStatsResponse) {
    state.nurseSexStats = payload;
  },
  [MutationTypes.SET_NURSES_EXPIRIENCE_RESPONSE](state: State, payload: GetNursesExperienceResponse) {
    state.nursesExperience = payload;
  },

  [MutationTypes.SET_INFREQUENT_PROCEDURES](state: State, payload: GetInfrequentProceduresResponse) {
    state.infrequentsProcedures = payload;
  },

  [MutationTypes.SET_MOST_POPULAR_PROCEDURES](state: State, payload: GetMostPopularProceduresResponse) {
    state.mostPopularProcedures = payload;
  },
  [MutationTypes.SET_PROCEDURE_LIST](state: State, payload: Array<Procedure>) {
    state.procedures = payload;
  },

  [MutationTypes.SET_PROCEDURE_PAGEINFO](state: State, payload: PagerResponse) {
    state.procedurePageInfo = payload;
  },

  [MutationTypes.SET_CATEGORY_LIST](state: State, payload: Array<Category>) {
    state.categoryList = payload;
  },

  [MutationTypes.SET_PROCEDURE_REFRESH](state: State, payload: boolean) {
    state.refreshProcedures = payload;
  },

  [MutationTypes.SET_ACTIVE_PROCEDURE](state: State, payload: Procedure) {
    state.activeProcedure = payload;
  },

  [MutationTypes.SET_CURRENT_USER](state: State, payload: User) {
    state.currentUser = payload;
  },

  [MutationTypes.SET_CURRENT_ROLES](state: State, payload: UserRole[]) {
    if (!state.impersonatedUser) {
      state.currentRoles = payload;
    }
  },

  SET_USER_SUB_IS_ACTIVE(state: State, payload: boolean) {
    state.currentUserIsActiveSub = payload;
  },

  [MutationTypes.SET_INSTITUTIONS_LIST](state: State, payload: Institution[]) {
    state.institutions = payload;
  },

  [MutationTypes.SET_INSTITUTIONS_PAGEINFO](state: State, payload: PagerResponse) {
    state.institutionPageInfo = payload;
  },

  [MutationTypes.SET_REFRESH_INSTITUTIONS](state: State, payload: boolean) {
    state.refreshInstitutions = payload;
  },

  [MutationTypes.SET_IMPERSONATED_USER](state: State, payload: UserRole) {
    state.impersonatedUser = true;
    state.currentRoles = [payload];
  },

  [MutationTypes.SET_STUDY_LIST](state: State, payload: Study[]) {
    state.studies = payload;
  },

  [MutationTypes.SET_STUDY_RES_LIST](state: State, payload: Study[]) {
    state.studyResults = payload;
  },

  [MutationTypes.SET_STUDY_PAGEINFO](state: State, payload: PagerResponse) {
    state.studyPageInfo = payload;
  },

  [MutationTypes.SET_ACTIVE_STUDY](state: State, payload: Study) {
    state.activeStudy = payload;
  },
  [MutationTypes.SET_LEARNING_OBJECTS_LIST](state: State, payload: LearningObject[]) {
    state.learningObjectLists = payload;
  },
  [MutationTypes.SET_LEARNING_OBJECTS_LIST_PAGE_INFO](state: State, payload: PagerResponse) {
    state.learningObjectsPageInfo = payload;
  },
  [MutationTypes.SET_FIVE_MOST_FREQUENTLY_PROCEDURES](state: State, payload: any) {
    state.fiveMostFrequentlyProcedures = payload;
  },
  [MutationTypes.SET_FIVE_LEAST_FREQUENTLY_PROCEDURES](state: State, payload: any) {
    state.fiveLeastFrequentlyProcedures = payload;
  },
  [MutationTypes.SET_SURVEY_RESULTS](state: State, payload: any) {
    state.surveyResults = payload;
  },
  [MutationTypes.SET_FIVE_MOST_ASSISTED_PROCEDURES](state: State, payload: any) {
    state.fiveMostAssistedProcedures = payload;
  },
  [MutationTypes.SET_SHIFT_INTENSITY_CHART](state: State, payload: any) {
    state.shiftIntensityChart = payload;
  },
  [MutationTypes.SET_SHIFT_TIREDNESS_CHART](state: State, payload: any) {
    state.shiftTirednessChart = payload;
  },
  [MutationTypes.SET_TABLE_SHOW_SIZE](state: State, payload: any) {
    state.tableShowSize = payload;
  },
  [MutationTypes.SET_PATIENTS_LIST](state: State, payload) {
    state.patientsList = payload;
  },
  [MutationTypes.SET_PATIENTS_PAGEINFO](state: State, payload) {
    state.patientsPageInfo = payload;
  },
  [MutationTypes.SET_VISITS_PAGEINFO](state: State, payload) {
    state.visitsPageInfo = payload;
  },
  [MutationTypes.SET_SHIFT_VISITS](state: State, payload) {
    state.shiftVisits = payload;
  },
  [MutationTypes.SET_NURSE_HISTORY_LOCATION](state: State, payload) {
    state.nurseHistoryLocation = payload;
  },
};

export default mutations;
